import * as React from 'react';
import axios from 'axios';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';

import {
  AppointmentStatus,
  EnhancedAppointment,
  Timezone,
} from '@bb/domain/dist/types';
import Loading from '@app/react-common/Loading';
import { createFormatDateFunction } from 'utils';

import styled from 'styled-components';
import {
  PropertyAnnouncementType,
  PropertyAnnouncementDeliveryLogType,
  RecipientType,
  RecipientTypes,
  Column,
} from './types';
import OfferRecipientsTable from './OfferRecipientsTable';

type EnhancedAppointmentVT = EnhancedAppointment & {
  timezone?: Timezone;
  displayStatus: string;
};
export interface OfferMessageRecipientProps {
  listingId: string;
  propertyAnnouncement?: PropertyAnnouncementType;
}

interface OfferMessageRecipientState {
  activeNavTab: RecipientTypes;
  propertyAnnouncementDeliveryLogs: PropertyAnnouncementDeliveryLogType[];
  loading: boolean;
  agents: Record<RecipientTypes, RecipientType[]>;
}

class OfferMessageRecipient extends React.Component<
  OfferMessageRecipientProps,
  OfferMessageRecipientState
> {
  constructor(props: OfferMessageRecipientProps) {
    super(props);

    const { propertyAnnouncement } = this.props;
    this.state = {
      activeNavTab: 'recipients',
      propertyAnnouncementDeliveryLogs: [],
      loading: true,
      agents: {
        recipients: propertyAnnouncement?.agents.recipients || [],
        removed: propertyAnnouncement?.agents.removed || [],
        unsubscribed: propertyAnnouncement?.agents.unsubscribed || [],
      },
    };
  }

  async componentDidMount() {
    const { propertyAnnouncement } = this.props;

    const [agents, propertyAnnouncementDeliveryLogs] = await Promise.all([
      !propertyAnnouncement && this.fetchAgents(),
      propertyAnnouncement && this.fetchDeliveryLogs(),
    ]);

    const updatedState = {
      ...this.state,
      loading: false,
    };

    if (agents) {
      updatedState.agents = agents;
    }
    if (propertyAnnouncementDeliveryLogs) {
      updatedState.propertyAnnouncementDeliveryLogs =
        propertyAnnouncementDeliveryLogs;
    }

    this.setState({ ...updatedState });
  }

  componentDidUpdate(prevProps: OfferMessageRecipientProps) {
    const { propertyAnnouncement } = this.props;
    if (prevProps.propertyAnnouncement?.id !== propertyAnnouncement?.id) {
      this.setState({
        agents: {
          recipients: propertyAnnouncement?.agents.recipients || [],
          removed: propertyAnnouncement?.agents.removed || [],
          unsubscribed: propertyAnnouncement?.agents.unsubscribed || [],
        },
      });
    }
  }

  fetchDeliveryLogs = async () => {
    const { listingId, propertyAnnouncement } = this.props;
    const { data } = await axios.get<PropertyAnnouncementDeliveryLogType[]>(
      `/listing/${listingId}/offer_message/${propertyAnnouncement.id}/delivery`,
    );
    return data;
  };

  fetchAgents = async () => {
    const newAgents = { ...this.state }.agents;
    const { listingId, propertyAnnouncement } = this.props;
    const sentTo = propertyAnnouncement?.sentTo || 'all';
    const { data } = await axios.get<{
      agentsSubscribed: RecipientType[];
      agentsUnsubscribed: RecipientType[];
      appointments: EnhancedAppointmentVT[];
    }>(`/listing/${listingId}/offer_agents`, { params: sentTo });
    const { agentsSubscribed, agentsUnsubscribed, appointments } = data;

    newAgents.recipients = agentsSubscribed;
    newAgents.unsubscribed = agentsUnsubscribed;

    if (propertyAnnouncement) {
      const coopEmailToAppointmentsMap = appointments
        .sort((a, b) => {
          if (!b) return -1;
          if (!a) return 1;
          return moment(b.time).unix() - moment(a.time).unix();
        })
        .reduce(
          (
            acc: { [index: string]: EnhancedAppointmentVT[] },
            appointment: EnhancedAppointmentVT,
          ) => {
            const coopEmail = appointment.coop && appointment.coop.email;

            if (!acc[coopEmail]) {
              acc[coopEmail] = [appointment];
            } else {
              acc[coopEmail].push(appointment);
            }

            return acc;
          },
          {},
        );

      // Map the recent appointment to each agent
      newAgents.recipients = newAgents.recipients.map(
        (agent: RecipientType) => {
          const enhancedAgent = { ...agent };
          const agentAppointments = coopEmailToAppointmentsMap[agent.email];
          if (!agentAppointments) return enhancedAgent;

          const latestAppointment = agentAppointments[0];

          enhancedAgent.appointment = {
            id: latestAppointment.id,
            status: latestAppointment.status,
            displayStatus: latestAppointment.displayStatus,
            time: latestAppointment.time,
            timezone:
              latestAppointment.timezone ?? latestAppointment.listing.timezone,
          };

          return enhancedAgent;
        },
      );

      newAgents.unsubscribed = newAgents.unsubscribed.map(
        (agent: RecipientType) => {
          const enhancedAgent = { ...agent };
          const agentAppointments = coopEmailToAppointmentsMap[agent.email];
          if (!agentAppointments) return enhancedAgent;

          const latestAppointment = agentAppointments[0];

          enhancedAgent.appointment = {
            id: latestAppointment.id,
            status: latestAppointment.status,
            displayStatus: latestAppointment.displayStatus,
            time: latestAppointment.time,
            timezone:
              latestAppointment.timezone ?? latestAppointment.listing.timezone,
          };

          return enhancedAgent;
        },
      );
    }

    return newAgents;
  };

  mapDeliveryLogsToAgent = () => {
    const { propertyAnnouncementDeliveryLogs } = this.state;
    const map = propertyAnnouncementDeliveryLogs.reduce<{
      [email: string]: PropertyAnnouncementDeliveryLogType[];
    }>((acc, log) => {
      const email = log.recipient.toLowerCase();

      if (!acc[email]) {
        acc[email] = [log];
      } else {
        acc[email].push(log);
      }

      return acc;
    }, {});
    return map;
  };

  render() {
    const {
      activeNavTab,
      propertyAnnouncementDeliveryLogs,
      agents: { recipients, removed, unsubscribed },
      loading,
    } = this.state;

    const TABS: { id: RecipientTypes; label: string }[] = [
      {
        id: 'recipients',
        label: 'Recipients',
      },
      {
        id: 'removed',
        label: 'Removed',
      },
      {
        id: 'unsubscribed',
        label: 'Unsubscribed',
      },
    ];

    const agentDetailsRenderer = (data: RecipientType) => (
      <td key={uuidv4()}>
        {data.name}
        <br />
        {data.org}
        <br />
        {data.email}
      </td>
    );
    const { mapDeliveryLogsToAgent } = this;
    let mappedLogs: ReturnType<typeof mapDeliveryLogsToAgent>;
    if (propertyAnnouncementDeliveryLogs) {
      mappedLogs = this.mapDeliveryLogsToAgent();
    }

    const TABLE_DATA: Record<
      RecipientTypes,
      {
        columns: Column<RecipientType>[];
        data: RecipientType[];
      }
    > = {
      recipients: {
        columns: [
          {
            id: 'agent-details',
            label: 'Agent Details',
            renderer: agentDetailsRenderer,
          },
          propertyAnnouncementDeliveryLogs && {
            id: 'appointment',
            label: 'Appointment',
            renderer: data => (
              <td key={uuidv4()}>
                {data.appointment && (
                  <>
                    <small>{data.appointment.displayStatus}</small>
                    <br />
                    <small>
                      {createFormatDateFunction('MMMM do, yyyy, h:mma')(
                        data.appointment.time,
                        data.appointment.timezone,
                      )}
                    </small>
                  </>
                )}
              </td>
            ),
          },
          propertyAnnouncementDeliveryLogs && {
            id: 'email-event',
            label: 'Email Event',
            renderer: data => (
              <td key={uuidv4()}>
                {mappedLogs[data.email?.toLowerCase()] &&
                  mappedLogs[data.email.toLowerCase()][0].event.toUpperCase()}
              </td>
            ),
          },
          propertyAnnouncementDeliveryLogs && {
            id: 'received-at',
            label: '',
            renderer: data => (
              <td key={uuidv4()}>
                {mappedLogs[data.email?.toLowerCase()] &&
                  moment(
                    mappedLogs[data.email.toLowerCase()][0].receivedAt,
                  ).format('MMM D, YYYY h:mm:ss A')}
              </td>
            ),
          },
        ],
        data: recipients,
      },
      removed: {
        columns: [
          {
            id: 'agent-details',
            label: 'Agent Details',
            renderer: agentDetailsRenderer,
          },
        ],
        data: removed,
      },
      unsubscribed: {
        columns: [
          {
            id: 'agent-details',
            label: 'Agent Details',
            renderer: agentDetailsRenderer,
          },
        ],
        data: unsubscribed,
      },
    };

    const { columns, data } = TABLE_DATA[activeNavTab];

    return (
      <div className="p-md" style={{ width: '100%', border: 'solid 1px #eee' }}>
        <div className="row">
          <div className="col-md-12">
            <ul className="nav nav-tabs nav-justified">
              {TABS.map(({ id, label }) => {
                const count = { ...this.state }.agents[id].length;
                return (
                  <li
                    key={id}
                    className={
                      activeNavTab === id
                        ? 'active offer-message-navtab-active'
                        : ''
                    }
                    onClick={() => this.setState({ activeNavTab: id })}
                  >
                    <a>
                      {label} ({count})
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
        <div className="row">
          <StyledTableContainer>
            {loading ? (
              <Loading size="fa-4x" />
            ) : (
              <OfferRecipientsTable
                emptyText={`No ${activeNavTab} agents`}
                columns={columns.filter(col => col)}
                data={data}
              />
            )}
          </StyledTableContainer>
        </div>
      </div>
    );
  }
}

export default OfferMessageRecipient;

const StyledTableContainer = styled.div.attrs({
  className: 'col-md-12',
})`
  overflow-x: scroll;
`;
